import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Popconfirm, Select, Space, Table, Tag, message } from "antd";
import { DeleteOutlined, ReloadOutlined, SwapOutlined } from "@ant-design/icons";
import { getData, getErrorMessage, isSuccess, rfidAdminApi } from "../../hooks/rfidAdminApi";
import "./RfidAdmin.css";

const AssignmentAdmin = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [tags, setTags] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const selectedClass = useMemo(() => classes.find((x) => String(x.id) === String(selectedClassId)), [classes, selectedClassId]);

  const loadBase = async () => {
    try {
      setLoading(true);
      const [classRes, tagRes] = await Promise.all([rfidAdminApi.getClasses(), rfidAdminApi.getTags()]);
      const classRows = getData(classRes);
      setClasses(classRows); setTags(getData(tagRes));
      if (!selectedClassId && classRows.length) setSelectedClassId(classRows[0].id);
    } catch (e) { messageApi.error(getErrorMessage(e)); }
    finally { setLoading(false); }
  };
  const loadClassData = async (classId = selectedClassId) => {
    if (!classId) { setStudents([]); setAssignments([]); return; }
    try {
      setLoading(true);
      const [studentRes, assignmentRes] = await Promise.all([rfidAdminApi.getStudents(classId), rfidAdminApi.getAssignments(classId)]);
      setStudents(getData(studentRes)); setAssignments(getData(assignmentRes));
    } catch (e) { messageApi.error(getErrorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadBase(); }, []);
  useEffect(() => { form.setFieldsValue({ classId: selectedClassId || undefined, studentId: undefined }); loadClassData(selectedClassId); }, [selectedClassId]);

  const handleAssign = async (values) => {
    try {
      const payload = { tagId: values.tagId, classId: values.classId || selectedClassId, studentId: values.studentId };
      const res = await rfidAdminApi.createAssignment(payload);
      if (!isSuccess(res)) throw new Error(res?.error || "배정 실패");
      messageApi.success("RFID 태그가 학생에게 배정되었습니다.");
      form.setFieldsValue({ tagId: undefined, studentId: undefined });
      await Promise.all([loadBase(), loadClassData(payload.classId)]);
    } catch (e) { messageApi.error(getErrorMessage(e)); }
  };
  const unassign = async (id) => {
    try { await rfidAdminApi.deleteAssignment(id); messageApi.success("배정을 해제했습니다."); await Promise.all([loadBase(), loadClassData()]); }
    catch (e) { messageApi.error(getErrorMessage(e)); }
  };

  const tagOptions = tags.filter((tag) => tag.is_active).map((tag) => ({
    value: tag.id,
    label: `${tag.tag_label} (${tag.epc})${tag.student_name ? ` · 현재 ${tag.class_name} ${tag.student_name}` : ""}`,
  }));
  const studentOptions = students.filter((s) => s.is_active).map((s) => ({
    value: s.id,
    label: `${s.student_no ? `${s.student_no}번 ` : ""}${s.name}${s.tag_label ? ` · 현재 ${s.tag_label}` : ""}`,
  }));
  const columns = [
    { title: "번호", dataIndex: "student_no", width: 80, render: (v) => v || "-" },
    { title: "학생", dataIndex: "student_name" },
    { title: "태그명", dataIndex: "tag_label" },
    { title: "EPC", dataIndex: "epc", render: (v) => <code>{v}</code> },
    { title: "배정일", dataIndex: "assigned_at", width: 180, render: (v) => v ? new Date(v).toLocaleString() : "-" },
    { title: "상태", width: 80, render: () => <Tag color="green">활성</Tag> },
    { title: "관리", width: 80, render: (_, r) => <Popconfirm title="배정 해제" onConfirm={() => unassign(r.id)}><Button danger size="small" icon={<DeleteOutlined />} /></Popconfirm> },
  ];

  return (
    <div className="rfid-page">
      {contextHolder}
      <div className="rfid-page-header"><div><h1 className="rfid-page-title">RFID 태그 배정</h1><p className="rfid-page-desc">등록된 EPC 태그를 학생에게 1:1로 연결합니다. 기존 학생/태그 배정은 자동 종료됩니다.</p></div><Button icon={<ReloadOutlined />} onClick={() => { loadBase(); loadClassData(); }}>새로고침</Button></div>
      <div className="rfid-grid-2">
        <Card title="태그 배정하기">
          <Form form={form} layout="vertical" onFinish={handleAssign}>
            <Form.Item name="classId" label="반" rules={[{ required: true }]}><Select value={selectedClassId || undefined} onChange={setSelectedClassId} options={classes.map((x) => ({ value: x.id, label: `${x.school_year} ${x.name}` }))} /></Form.Item>
            <Form.Item name="studentId" label="학생" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={studentOptions} /></Form.Item>
            <Form.Item name="tagId" label="RFID 태그" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={tagOptions} /></Form.Item>
            <Button type="primary" htmlType="submit" block icon={<SwapOutlined />}>배정 저장</Button>
          </Form>
        </Card>
        <Card title="현재 선택된 반">
          {selectedClass ? <Space direction="vertical"><b>{selectedClass.school_year} {selectedClass.name}</b><span className="rfid-muted">학생 {students.length}명 / 활성 배정 {assignments.length}건</span><span className="rfid-muted">태그 관리에서 EPC를 먼저 등록하세요.</span></Space> : <span className="rfid-muted">반을 선택해주세요.</span>}
        </Card>
      </div>
      <Card className="rfid-section-gap" title="활성 배정 목록"><Table rowKey="id" loading={loading} columns={columns} dataSource={assignments} pagination={{ pageSize: 12 }} scroll={{ x: 900 }} /></Card>
    </div>
  );
};
export default AssignmentAdmin;
